// The reharm LAYER SWITCHER — the transport panel behind the "harmony" chip.
// (docs/reharm-plan.md §8, entry point 2; Phase 0.)
//
// A tap-list in the Style panel's shape: Original, then each layer, the
// active one marked; New / Duplicate / Rename / Delete beneath. Every action
// is an ordinary undoable score edit through the store, and switching is
// live where the onsets hold (useBandArrangement decides, as for any harmony
// edit) — which is what makes A/B-ing two harmonizations while the band
// loops two taps. Rename is inline (no window.prompt — the playlist rule),
// delete is a two-step armed button that disarms on blur.

import { useState } from 'react';
import { reharmLayers } from '@/music/score/reharm';
import { useStore } from '@/state/store';
import { useFlag } from '@/auth/flags';
import { TransportControlButton } from '@/components/TransportBar';
import { ReharmPassagePanel } from '@/components/ReharmPassagePanel';

export function ReharmLayersPanel() {
  const score = useStore((s) => s.score);
  const createReharmLayer = useStore((s) => s.createReharmLayer);
  const activateReharmLayer = useStore((s) => s.activateReharmLayer);
  const renameReharmLayer = useStore((s) => s.renameReharmLayer);
  const deleteReharmLayer = useStore((s) => s.deleteReharmLayer);
  const duplicateReharmLayer = useStore((s) => s.duplicateReharmLayer);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [armedDeleteId, setArmedDeleteId] = useState<string | null>(null);

  const layers = reharmLayers(score);
  const active = score.reharm?.active ?? null;
  // The reharm ENGINE is flag-gated (#327: parked for pilot students); the
  // Analysis switch and the layer list stay — layers are score data.
  const reharmEnabled = useFlag('reharm');
  const auditioning = useStore((s) => s.reharmPreview !== null);
  const showAnalysis = useStore((s) => s.showAnalysis);
  const setShowAnalysis = useStore((s) => s.setShowAnalysis);

  const commitRename = (id: string, value: string) => {
    renameReharmLayer(id, value);
    setRenamingId(null);
  };

  return (
    <div className="panel style-list reharm-layers" data-testid="reharm-layers">
      {/* The analysis lane — key regions, romans and cadence brackets under
          each score system — lives with the harmony it explains (user ruling
          Sep 2026, moved here from the view settings and the Score Studio's
          View row): reharmonizing and reading the result are one activity. */}
      <label className="switch reharm-switch reharm-analysis">
        <input
          type="checkbox"
          role="switch"
          checked={showAnalysis}
          aria-checked={showAnalysis}
          aria-label="Analysis"
          data-testid="analysis-switch"
          onChange={(e) => setShowAnalysis(e.target.checked)}
        />
        <strong>Analysis</strong>
      </label>
      <TransportControlButton
        variant="row"
        active={active === null}
        aria-current={active === null}
        data-testid="reharm-layer-original"
        onClick={() => activateReharmLayer(null)}
      >
        Original
        {active === null && <span className="style-check">✓</span>}
      </TransportControlButton>
      {layers.map((layer) =>
        renamingId === layer.id ? (
          <div className="reharm-layer-row" key={layer.id}>
            <input
              className="input"
              defaultValue={layer.name}
              aria-label={`Rename ${layer.name}`}
              data-testid="reharm-layer-rename-input"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitRename(layer.id, e.currentTarget.value);
                if (e.key === 'Escape') setRenamingId(null);
              }}
              onBlur={(e) => commitRename(layer.id, e.target.value)}
            />
          </div>
        ) : (
          <div className="reharm-layer-row" key={layer.id}>
            <TransportControlButton
              variant="row"
              active={active === layer.id}
              aria-current={active === layer.id}
              data-testid="reharm-layer"
              data-layer-id={layer.id}
              data-preview={layer.preview ? 'true' : undefined}
              onClick={() => activateReharmLayer(layer.id)}
            >
              {layer.name}
              <span className="muted reharm-layer-count">
                {' · '}
                {layer.preview
                  ? 'live'
                  : `${Object.keys(layer.overrides).length} bar${
                      Object.keys(layer.overrides).length === 1 ? '' : 's'
                    }`}
              </span>
              {active === layer.id && <span className="style-check">✓</span>}
            </TransportControlButton>
            {/* The live reharm is switched off or saved in the Reharmonize
                section below; renaming or deleting it here as well would be
                two names for one decision. */}
            {!layer.preview && (
              <>
                <button
                  type="button"
                  className="btn btn-sm btn-ghost"
                  aria-label={`Rename ${layer.name}`}
                  data-testid="reharm-layer-rename"
                  onClick={() => setRenamingId(layer.id)}
                >
                  Rename
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${armedDeleteId === layer.id ? 'btn-danger' : 'btn-ghost'}`}
                  aria-label={
                    armedDeleteId === layer.id
                      ? `Confirm delete ${layer.name}`
                      : `Delete ${layer.name}`
                  }
                  data-testid="reharm-layer-delete"
                  onBlur={() => setArmedDeleteId(null)}
                  onClick={() => {
                    if (armedDeleteId === layer.id) {
                      deleteReharmLayer(layer.id);
                      setArmedDeleteId(null);
                    } else {
                      setArmedDeleteId(layer.id);
                    }
                  }}
                >
                  {armedDeleteId === layer.id ? 'Delete?' : 'Delete'}
                </button>
              </>
            )}
          </div>
        ),
      )}
      <div className="reharm-layer-actions">
        <button
          type="button"
          className="btn btn-sm"
          data-testid="reharm-layer-new"
          onClick={() => createReharmLayer()}
        >
          New layer
        </button>
        {active !== null && !auditioning && (
          <button
            type="button"
            className="btn btn-sm"
            data-testid="reharm-layer-duplicate"
            onClick={() => duplicateReharmLayer(active)}
          >
            Duplicate
          </button>
        )}
      </div>
      {reharmEnabled && <ReharmPassagePanel />}
    </div>
  );
}
